import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, Sparkles, Search as SearchIcon, Smartphone, Eye, PlayCircle, Wand2,
} from "lucide-react";

const Badge = ({ icon: Icon, label, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-300 bg-white/5 border border-white/10 px-2.5 py-1">
    <Icon size={12} className="text-gold" /> {label}
  </span>
);

export default function MarketplaceCard({ template, index = 0 }) {
  const navigate = useNavigate();
  const slug = template.slug || template.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col bg-navy-800 border border-white/10 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-black/40"
      data-testid={`template-card-${template.id}`}
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden cursor-pointer" onClick={() => navigate(`/template/${slug}`)}>
        {template.preview_image ? (
          <img src={template.preview_image} alt={template.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-navy-700 text-gray-500 text-xs uppercase tracking-wide">{template.category}</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/10 to-transparent" />
        {template.badge && (
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.15em] bg-gold text-navy px-3 py-1 font-bold">{template.badge}</span>
        )}
        {/* hover overlay actions */}
        <div className="absolute inset-0 flex items-center justify-center gap-3 bg-navy/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button onClick={(e) => { e.stopPropagation(); navigate(`/template/${slug}`); }} data-testid={`preview-${template.id}`}
            className="flex items-center gap-2 bg-white text-navy px-4 py-2.5 text-sm font-semibold hover:bg-gold transition-colors">
            <Eye size={15} /> Preview
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gold uppercase tracking-[0.15em]">{template.category}</span>
          {template.reviews > 0 ? (
            <span className="flex items-center gap-1 text-sm text-white" data-testid={`rating-${template.id}`}>
              <Star size={14} className="fill-gold text-gold" /> {Number(template.rating || 0).toFixed(1)}
              <span className="text-gray-500 text-xs">({template.reviews})</span>
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 uppercase tracking-wide border border-white/10 px-2 py-0.5" data-testid={`rating-${template.id}`}>New</span>
          )}
        </div>

        <h3 className="font-heading text-xl text-white mb-2 group-hover:text-gold transition-colors cursor-pointer" onClick={() => navigate(`/template/${slug}`)}>
          {template.title}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 mb-4">{template.description}</p>

        {/* Feature badges */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid={`badges-${template.id}`}>
          {template.ai_ready && <Badge icon={Sparkles} label="AI Ready" testid={`badge-ai-${template.id}`} />}
          {template.seo_ready && <Badge icon={SearchIcon} label="SEO Ready" testid={`badge-seo-${template.id}`} />}
          {template.responsive && <Badge icon={Smartphone} label="Responsive" testid={`badge-responsive-${template.id}`} />}
        </div>

        {/* Custom-build pricing — service oriented, no template purchase */}
        <div className="flex items-end justify-between mb-5 pt-4 border-t border-white/10 mt-auto">
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Custom build</div>
            <div className="font-heading text-2xl text-gold">Starting at $997</div>
          </div>
          <div className="text-right text-[11px] text-gray-500 leading-snug">Customized<br/>& launched for you</div>
        </div>

        {/* Actions */}
        <div className={`grid ${template.live_demo ? "grid-cols-2" : "grid-cols-1"} gap-3 mb-3`}>
          <button onClick={() => navigate(`/template/${slug}`)} data-testid={`preview-cta-${template.id}`}
            className="flex items-center justify-center gap-2 border border-white/25 text-white py-3 text-sm font-medium hover:bg-white/5 hover:border-gold/50 transition-colors">
            <Eye size={15} /> Preview
          </button>
          {template.live_demo && (
            <button onClick={() => navigate(`/marketplace/demo/${slug}`)} data-testid={`demo-${template.id}`}
              className="flex items-center justify-center gap-2 border border-white/25 text-white py-3 text-sm font-medium hover:bg-white/5 hover:border-gold/50 transition-colors">
              <PlayCircle size={15} /> Live Demo
            </button>
          )}
        </div>

        <button onClick={() => navigate(`/start?design=${encodeURIComponent(template.title)}`)} data-testid={`customize-${template.id}`}
          className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 text-sm font-semibold hover:bg-gold-light transition-colors">
          <Wand2 size={15} /> Build This for My Business
        </button>
      </div>
    </motion.div>
  );
}
